/**
 * Unsubscribe Page
 * 
 * Handles email unsubscribe requests from marketing emails.
 * Route: /unsubscribe/:encodedEmail
 * 
 * Requires user to confirm by entering their email address.
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Mail, ArrowLeft, Bell, BellOff } from 'lucide-react';
import { API_BASE_URL } from '../config/api';

const Unsubscribe: React.FC = () => {
  const { encodedEmail } = useParams<{ encodedEmail: string }>();
  const [status, setStatus] = useState<'confirm' | 'processing' | 'success' | 'error' | 'already'>('confirm');
  const [decodedEmail, setDecodedEmail] = useState<string>('');
  const [inputEmail, setInputEmail] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');

  // Decode the email from URL on mount
  useEffect(() => {
    if (encodedEmail) {
      try {
        // Add padding if necessary for base64
        let padded = encodedEmail;
        const padding = 4 - (encodedEmail.length % 4);
        if (padding !== 4) {
          padded += '='.repeat(padding);
        }
        const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
        setDecodedEmail(decoded);
      } catch (e) {
        console.error('Failed to decode email:', e);
      }
    }
  }, [encodedEmail]);

  const handleUnsubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');

    // Validate email matches
    if (inputEmail.toLowerCase().trim() !== decodedEmail.toLowerCase().trim()) {
      setEmailError('The email address you entered does not match our records.');
      return;
    }

    setStatus('processing');

    try {
      const response = await fetch(`${API_BASE_URL}/unsubscribe/${encodedEmail}/confirm`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: inputEmail.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.already_unsubscribed) {
          setStatus('already');
        } else {
          setStatus('success');
        }
      } else {
        setStatus('error');
        setErrorMessage(data.detail || 'Failed to process unsubscribe request');
      }
    } catch (error) {
      console.error('Unsubscribe error:', error);
      setStatus('error');
      setErrorMessage('Unable to process your request. Please try again later.');
    }
  };

  // Mask email for display (show first 2 chars + domain)
  const getMaskedEmail = (email: string) => {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const maskedLocal = local.slice(0, 2) + '•'.repeat(Math.max(local.length - 2, 3));
    return `${maskedLocal}@${domain}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 md:p-10 max-w-md w-full">
        
        {/* Confirmation Form */}
        {status === 'confirm' && (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BellOff className="w-8 h-8 text-gray-600" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                Unsubscribe from emails
              </h1>
              <p className="text-gray-600">
                You're about to unsubscribe from promotional emails.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                <strong>What this means:</strong> You will no longer receive appointment reminders, 
                promotional offers, or practice updates via email. Important account notifications 
                may still be sent.
              </p>
            </div>

            <form onSubmit={handleUnsubscribe}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm your email address
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  {decodedEmail ? `Hint: ${getMaskedEmail(decodedEmail)}` : 'Enter the email address you wish to unsubscribe'}
                </p>
                <input
                  type="email"
                  id="email"
                  value={inputEmail}
                  onChange={(e) => {
                    setInputEmail(e.target.value);
                    setEmailError('');
                  }}
                  placeholder="your@email.com"
                  required
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    emailError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {emailError && (
                  <p className="mt-1 text-sm text-red-600">{emailError}</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                Confirm Unsubscribe
              </button>
            </form>

            <p className="text-center text-sm text-gray-500 mt-6">
              Changed your mind?{' '}
              <Link to="/" className="text-blue-600 hover:underline">
                Return to homepage
              </Link>
            </p>
          </>
        )}

        {/* Processing State */}
        {status === 'processing' && (
          <div className="text-center py-8">
            <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Processing your request...</p>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              You've been unsubscribed
            </h1>
            <p className="text-gray-600 mb-4">
              We've removed <strong>{decodedEmail}</strong> from our mailing list.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              You will no longer receive promotional emails from us. 
              If you change your mind, you can update your preferences in your account settings.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Return to homepage
            </Link>
          </div>
        )}

        {/* Already Unsubscribed State */}
        {status === 'already' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Already unsubscribed
            </h1>
            <p className="text-gray-600 mb-4">
              This email address was already removed from our mailing list.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              No further action is needed.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Return to homepage
            </Link>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-4">
              {errorMessage}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Please try again or contact us if the problem persists.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => setStatus('confirm')}
                className="inline-flex items-center justify-center gap-2 bg-gray-900 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                Try again
              </button>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 text-gray-600 hover:text-gray-700"
              >
                <ArrowLeft className="w-4 h-4" />
                Return to homepage
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;
